import React, { useState, useEffect } from "react";
import api from "../../../services/AxiosConnection";
import { FaTrash, FaPencilAlt, FaSave, FaPlus } from "react-icons/fa";
import { FiX } from "react-icons/fi";
import useAuth from "../../../hooks/useAuth";
import { Vortex } from "react-loader-spinner";

const EmailEntryForm = ({
  email,
  onEdit,
  onSave,
  onCancel,
  onDelete,
  isEditing,
  error,
  label,
  onEmailChange,
  isSuperAdmin,
  isFirstDefault,
  index,
}) => {
  const [localValue, setLocalValue] = useState(email.value);

  useEffect(() => {
    setLocalValue(email.value);
  }, [email.value]);

  const handleInputChange = (e) => {
    if (!isSuperAdmin) return;
    const value = e.target.value;
    setLocalValue(value);
    onEmailChange(value);
  };

  return (
    <div className="bg-white p-3 rounded-lg shadow-xs border border-gray-100 hover:shadow-sm transition-all duration-150 mb-2 relative">
      <div className="absolute top-0 left-0 bg-blue-600 text-white rounded-br-lg rounded-tl-lg px-3 py-1 text-xs font-semibold shadow-sm">
        {index + 1}
      </div>
      <div className="pl-8">
        <p className="text-gray-500 text-xs font-medium uppercase tracking-wider mb-1">
          {label}
        </p>
        <div className="flex flex-col md:flex-row md:items-center gap-2">
          <div className="flex-grow">
            <input
              type="email"
              value={localValue}
              onChange={handleInputChange}
              disabled={!isEditing || !isSuperAdmin}
              placeholder="name@example.com"
              className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-transparent transition-all duration-150 disabled:bg-gray-50 disabled:text-gray-400"
            />
            {error && (
              <p className="text-red-500 text-xs mt-1 animate-fadeIn">
                {error}
              </p>
            )}
          </div>
          {isSuperAdmin && (
            <div className="flex gap-1.5">
              <button
                onClick={() => (isEditing ? onSave() : onEdit())}
                className={`px-3 py-1.5 rounded-md flex items-center gap-1 text-xs font-medium transition-all duration-150 ${isEditing
                    ? "bg-emerald-500 text-white hover:bg-emerald-600 shadow-xs hover:shadow-emerald-100"
                    : "bg-blue-500 text-white hover:bg-blue-600 shadow-xs hover:shadow-blue-100"
                  }`}
              >
                {isEditing ? (
                  <>
                    <FaSave className="text-xs" />
                    Save
                  </>
                ) : email.isNew ? (
                  <>
                    <FaPlus className="text-xs" />
                    Add
                  </>
                ) : (
                  <FaPencilAlt className="text-xs" />
                )}
              </button>
              {isEditing && !isFirstDefault && (
                <button
                  onClick={onCancel}
                  className="px-3 py-1.5 bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200 flex items-center gap-1 text-xs font-medium transition-all duration-150 shadow-xs hover:shadow-gray-100"
                >
                  <FiX className="text-xs" />
                  Cancel
                </button>
              )}
              {!isEditing && !email.isNew && (
                <button
                  onClick={onDelete}
                  className="p-1.5 bg-red-500 text-white rounded-md hover:bg-red-600 flex items-center justify-center transition-all duration-150 shadow-xs hover:shadow-red-100"
                >
                  <FaTrash className="text-xs" />
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ReportSection = ({
  title,
  emails,
  onEdit,
  onSave,
  editingId,
  onEmailChange,
  errors,
  labelPrefix,
  onAddMore,
  isLoading,
  handleCancel,
  onDelete,
  type,
  isSuperAdmin,
}) => {
  return (
    <div className="bg-white p-4 rounded-lg shadow-xs border border-gray-100">
      <h2 className="text-base font-semibold text-gray-800 mb-3 pb-2 border-b border-gray-100">
        {title}
      </h2>
      {emails.length === 0 && !isSuperAdmin ? (
        <div className="text-center text-gray-400 text-xs py-3">
          No email recipients configured
        </div>
      ) : (
        emails.map((email, index) => (
          <EmailEntryForm
            key={email.id}
            email={email}
            onEdit={() => onEdit(email.id)}
            onSave={() => onSave(email.id)}
            onDelete={() => onDelete(email.id, type)}
            onCancel={() => handleCancel(email.id, type)}
            isEditing={editingId === email.id}
            error={errors[email.id]}
            label={`${labelPrefix} to be sent to:`}
            onEmailChange={(value) => onEmailChange(email.id, value, type)}
            isSuperAdmin={isSuperAdmin}
            isFirstDefault={emails.length === 1 && email.isNew}
            index={index}
          />
        ))
      )}
      {isSuperAdmin && (
        <div className="flex justify-center mt-3">
          <button
            onClick={onAddMore}
            className="px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-1 text-xs font-medium transition-all duration-150 shadow-xs hover:shadow-indigo-100"
          >
            <FaPlus className="text-xs" /> Add More
          </button>
        </div>
      )}
    </div>
  );
};

const ReportConfigForm = () => {
  const { user, selectedSite } = useAuth();
  const isSuperAdmin = user?.role === "Super Admin";
  const [weeklyEmails, setWeeklyEmails] = useState([]);
  const [monthlyEmails, setMonthlyEmails] = useState([]);
  const [editingWeeklyId, setEditingWeeklyId] = useState(null);
  const [editingMonthlyId, setEditingMonthlyId] = useState(null);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const fetchEmails = async (type) => {
    try {
      setIsLoading(true);
      const response = await api.get(
        type === "weekly"
          ? `/control-panel/getWeeklyReportDetails?siteDb=${selectedSite}`
          : `/control-panel/getMonthlyReportDetails?siteDb=${selectedSite}`
      );

      let emails = response.data.data.map((email) => ({
        id: String(email.ID),
        value: email.Email,
        originalValue: email.Email,
        isNew: false,
      }));

      if (emails.length === 0 && isSuperAdmin) {
        const newEmail = {
          id: `${type === "weekly" ? "w" : "m"}${Date.now()}`,
          value: "",
          originalValue: "",
          isNew: true,
        };
        emails = [newEmail];
        if (type === "weekly") {
          setEditingWeeklyId(newEmail.id);
        } else {
          setEditingMonthlyId(newEmail.id);
        }
      }

      if (type === "weekly") {
        setWeeklyEmails(emails);
      } else {
        setMonthlyEmails(emails);
      }
    } catch (error) {
      console.error(`Error fetching ${type} emails:`, error);
      if (isSuperAdmin) {
        const newEmail = {
          id: `${type === "weekly" ? "w" : "m"}${Date.now()}`,
          value: "",
          originalValue: "",
          isNew: true,
        };
        if (type === "weekly") {
          setWeeklyEmails([newEmail]);
          setEditingWeeklyId(newEmail.id);
        } else {
          setMonthlyEmails([newEmail]);
          setEditingMonthlyId(newEmail.id);
        }
      } else {
        if (type === "weekly") {
          setWeeklyEmails([]);
        } else {
          setMonthlyEmails([]);
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    let isMounted = true;

    if (selectedSite) {
      Promise.all([fetchEmails("weekly"), fetchEmails("monthly")]).catch((error) =>
        console.error("Error fetching emails:", error)
      );
    } else {
      if (isMounted) {
        setWeeklyEmails([]);
        setMonthlyEmails([]);
        setIsLoading(false);
      }
    }

    return () => {
      isMounted = false;
    };
  }, [selectedSite]);

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleEdit = (id, type) => {
    if (!isSuperAdmin) return;
    if (type === "weekly") {
      setEditingWeeklyId(id);
    } else {
      setEditingMonthlyId(id);
    }
    setErrors((prev) => ({ ...prev, [id]: "" }));
  };

  const handleSave = async (id, type) => {
    if (!isSuperAdmin) return;
    const emailList = type === "weekly" ? [...weeklyEmails] : [...monthlyEmails];
    const email = emailList.find((e) => e.id === id);

    if (!email.value) {
      setErrors((prev) => ({ ...prev, [id]: "Email is required" }));
      return;
    }
    if (!validateEmail(email.value)) {
      setErrors((prev) => ({ ...prev, [id]: "Please enter a valid email address" }));
      return;
    }

    try {
      setIsLoading(true);
      const formData = {
        siteDb: selectedSite,
        Email: email.value,
      };

      const isNew = email.id.startsWith("w") || email.id.startsWith("m");
      let apiUrl = isNew
        ? type === "weekly"
          ? "/control-panel/insertWeeklyReportDetails"
          : "/control-panel/insertMonthlyReportDetails"
        : type === "weekly"
          ? "/control-panel/updateWeeklyReportDetails"
          : "/control-panel/updateMonthlyReportDetails";
      let method = isNew ? "post" : "put";

      if (!isNew) {
        formData.ID = email.id;
      }

      const response = await api[method](apiUrl, formData);

      if (isNew && response.data?.ID) {
        const updatedList = emailList.map((e) =>
          e.id === id ? { ...e, id: String(response.data.ID), isNew: false } : e
        );
        if (type === "weekly") {
          setWeeklyEmails(updatedList);
        } else {
          setMonthlyEmails(updatedList);
        }
      }

      if (type === "weekly") {
        setEditingWeeklyId(null);
      } else {
        setEditingMonthlyId(null);
      }

      await fetchEmails(type);
    } catch (error) {
      console.error("Error saving email:", error);
      setErrors((prev) => ({ ...prev, [id]: "Failed to save email" }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddMore = (type) => {
    if (!isSuperAdmin) return;
    const newEmail = {
      id: `${type === "weekly" ? "w" : "m"}${Date.now()}`,
      value: "",
      originalValue: "",
      isNew: true,
    };

    if (type === "weekly") {
      setWeeklyEmails((prev) => [...prev, newEmail]);
      setEditingWeeklyId(newEmail.id);
    } else {
      setMonthlyEmails((prev) => [...prev, newEmail]);
      setEditingMonthlyId(newEmail.id);
    }
  };

  const handleEmailChange = (id, value, type) => {
    if (!isSuperAdmin) return;
    const updateEmails = (emails) =>
      emails.map((email) =>
        email.id === id ? { ...email, value } : email
      );

    if (type === "weekly") {
      setWeeklyEmails((prev) => updateEmails(prev));
    } else {
      setMonthlyEmails((prev) => updateEmails(prev));
    }

    setErrors((prev) => ({ ...prev, [id]: "" }));
  };

  const handleDelete = async (id, type) => {
    if (!isSuperAdmin) return;
    const isConfirmed = window.confirm(
      "Are you sure you want to delete this email?"
    );

    if (!isConfirmed) return;

    try {
      setIsLoading(true);
      const apiUrl =
        type === "weekly"
          ? "/control-panel/deleteWeeklyReportDetails"
          : "/control-panel/deleteMonthlyReportDetails";

      const formData = {
        siteDb: selectedSite,
        ID: Number(id),
      };

      await api.delete(apiUrl, { data: formData });

      if (type === "weekly") {
        setWeeklyEmails((prev) => prev.filter((email) => email.id !== id));
      } else {
        setMonthlyEmails((prev) => prev.filter((email) => email.id !== id));
      }

      await fetchEmails(type);
    } catch (error) {
      console.error("Error deleting email:", error);
      setErrors((prev) => ({ ...prev, [id]: "Failed to delete email" }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = (id, type) => {
    if (!isSuperAdmin) return;
    const idStr = String(id);
    const emailList = type === "weekly" ? [...weeklyEmails] : [...monthlyEmails];
    const email = emailList.find((e) => e.id === idStr);

    if (email.isNew) {
      if (type === "weekly") {
        setWeeklyEmails((prev) => prev.filter((e) => e.id !== idStr));
        setEditingWeeklyId(null);
      } else {
        setMonthlyEmails((prev) => prev.filter((e) => e.id !== idStr));
        setEditingMonthlyId(null);
      }
    } else {
      const resetEmail = (emails) =>
        emails.map((email) =>
          email.id === idStr
            ? { ...email, value: email.originalValue }
            : email
        );

      if (type === "weekly") {
        setWeeklyEmails((prev) => resetEmail(prev));
        setEditingWeeklyId(null);
      } else {
        setMonthlyEmails((prev) => resetEmail(prev));
        setEditingMonthlyId(null);
      }
    }

    setErrors((prev) => ({ ...prev, [idStr]: "" }));
  };

  return (
    <div className="max-w-4xl mx-auto px-3 py-4 space-y-4">
      {isLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <Vortex
            visible={true}
            height={80}
            width={80}
            ariaLabel="vortex-loading"
            wrapperStyle={{}}
            wrapperClass="vortex-wrapper"
            colors={["#2563eb", "#1e40af", "#60a5fa", "#3b82f6", "#1d4ed8", "#93c5fd"]}
          />
        </div>
      )}
      <div className="bg-blue-50 p-4 rounded-lg mb-4">
        <h2 className="text-base font-semibold text-blue-800 mb-1">
          Report Configuration
        </h2>
        <p className="text-gray-600 text-xs">
          {isSuperAdmin
            ? "Configure email recipients for weekly and monthly reports."
            : "View email recipients for weekly and monthly reports."}
        </p>
      </div>

      <ReportSection
        title="Weekly Report"
        emails={weeklyEmails}
        onEdit={(id) => handleEdit(id, "weekly")}
        onSave={(id) => handleSave(id, "weekly")}
        editingId={editingWeeklyId}
        onEmailChange={handleEmailChange}
        errors={errors}
        labelPrefix="Weekly Report"
        onAddMore={() => handleAddMore("weekly")}
        isLoading={isLoading}
        handleCancel={handleCancel}
        onDelete={handleDelete}
        type="weekly"
        isSuperAdmin={isSuperAdmin}
      />

      <ReportSection
        title="Monthly Report"
        emails={monthlyEmails}
        onEdit={(id) => handleEdit(id, "monthly")}
        onSave={(id) => handleSave(id, "monthly")}
        editingId={editingMonthlyId}
        onEmailChange={handleEmailChange}
        errors={errors}
        labelPrefix="Monthly Report"
        onAddMore={() => handleAddMore("monthly")}
        isLoading={isLoading}
        handleCancel={handleCancel}
        onDelete={handleDelete}
        type="monthly"
        isSuperAdmin={isSuperAdmin}
      />
    </div>
  );
};

export default ReportConfigForm;